/* Wig Try-On — shared core
 * Loaded once. Used by the PDP modal and the collection grid.
 *
 * Privacy model: the customer's photo and every look generated from it live in
 * sessionStorage only. They are sent to the endpoint for the moment of
 * generation and never written to our storage. Preset looks (starter model x
 * product) contain no customer data and are cached server-side.
 */
window.WigTryOn = (function () {
  const MAX_EDGE = 1152;
  const JPEG_Q = 0.86;
  const PHOTO_KEY = 'wt:photo';
  const LOOKS_KEY = 'wt:looks';
  const TRAY_MAX = 4;
  const TIMEOUT_MS = 45000;
  const TONE_TOLERANCE = 26;   // mean RGB distance allowed between input and output skin.
                               // Framing shifts between input and output move this a lot,
                               // so keep it loose enough to catch real drift only.

  /* ---------- analytics ----------
   * Fans out to three places: the Shopify custom pixel (via analytics.publish),
   * GTM's dataLayer, and Clarity as a session tag so you can filter recordings
   * down to people who actually used this. */

  const BUCKET_KEY = 'wt:bucket';

  function bucket() {
    try {
      let b = localStorage.getItem(BUCKET_KEY);
      if (!b) {
        b = Math.random() < 0.5 ? 'tryon' : 'holdout';
        localStorage.setItem(BUCKET_KEY, b);
      }
      return b;
    } catch { return 'tryon'; }
  }

  let tagged = false;

  function track(event, props) {
    const payload = { ...props, bucket: bucket(), ts: Date.now() };

    try { window.Shopify?.analytics?.publish?.(event, payload); } catch {}
    try { (window.dataLayer = window.dataLayer || []).push({ event, ...payload }); } catch {}

    try {
      if (typeof window.clarity === 'function') {
        if (!tagged) {
          window.clarity('set', 'tryon_user', 'true');
          window.clarity('set', 'tryon_bucket', payload.bucket);
          // Prioritize these sessions for recording — they're the ones worth watching.
          window.clarity('upgrade', 'virtual_try_on');
          tagged = true;
        }
        window.clarity('event', event);
        if (props?.productTitle) window.clarity('set', 'tryon_product', String(props.productTitle));
      }
    } catch {}
  }

  /* ---------- session store ---------- */

  const store = {
    getPhoto() {
      try { return sessionStorage.getItem(PHOTO_KEY); } catch { return null; }
    },
    setPhoto(dataUrl) {
      track('wig_tryon_photo_uploaded', {});
      try {
        sessionStorage.setItem(PHOTO_KEY, dataUrl);
        // A new face invalidates every look built from the old one.
        sessionStorage.removeItem(LOOKS_KEY);
      } catch { /* private mode — feature still works, just not across pages */ }
      emit();
    },
    clear() {
      try {
        sessionStorage.removeItem(PHOTO_KEY);
        sessionStorage.removeItem(LOOKS_KEY);
      } catch {}
      emit();
    },
    looks() {
      try { return JSON.parse(sessionStorage.getItem(LOOKS_KEY) || '{}'); } catch { return {}; }
    },
    getLook(key) {
      return store.looks()[key] || null;
    },
    setLook(key, record) {
      const all = store.looks();
      all[key] = record;
      try { sessionStorage.setItem(LOOKS_KEY, JSON.stringify(all)); }
      catch { prune(all, key, record); }
      emit();
    },
    tray() {
      return Object.entries(store.looks())
        .map(([key, v]) => ({ key, ...v }))
        .sort((a, b) => b.at - a.at)
        .slice(0, TRAY_MAX);
    },
    subscribe(fn) { listeners.push(fn); return () => listeners.splice(listeners.indexOf(fn), 1); }
  };

  const listeners = [];
  function emit() { listeners.forEach((fn) => { try { fn(); } catch {} }); }

  function prune(all, key, record) {
    // sessionStorage full: drop oldest until the new record fits.
    const ordered = Object.entries(all).sort((a, b) => a[1].at - b[1].at);
    while (ordered.length) {
      delete all[ordered.shift()[0]];
      try { sessionStorage.setItem(LOOKS_KEY, JSON.stringify({ ...all, [key]: record })); return; }
      catch {}
    }
  }

  /* ---------- input handling ---------- */

  function downscale(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', JPEG_Q));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image could not be read. Try a JPG or PNG.')); };
      img.src = url;
    });
  }

  /* Pre-flight face check. Uses the native FaceDetector where the browser has it
     (Chrome/Edge on Android and desktop). Elsewhere it passes and we rely on the
     model's own guard — better than blocking a real customer on a missing API. */
  async function checkFace(dataUrl) {
    if (!('FaceDetector' in window)) return { ok: true, checked: false };
    try {
      const bmp = await createImageBitmap(await (await fetch(dataUrl)).blob());
      const faces = await new window.FaceDetector({ fastMode: true }).detect(bmp);
      if (faces.length === 0) {
        return { ok: false, message: 'No face found. Use a photo facing the camera, chin level, in good light.' };
      }
      if (faces.length > 1) {
        return { ok: false, message: 'More than one face in that photo. Crop it down to just you.' };
      }
      const box = faces[0].boundingBox;
      if (Math.min(box.width, box.height) < 120) {
        return { ok: false, message: 'Your face is too small in that shot. Move closer or crop in.' };
      }
      return { ok: true, checked: true };
    } catch {
      return { ok: true, checked: false };
    }
  }

  /* ---------- quality gate ---------- */

  /* Mean color of the mid-face band, sampled the same way in both images.
     Catches the failure that matters most: the model lightening skin. */
  async function faceTone(src) {
    const img = await load(src);
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    // Centre band, upper third — cheeks and nose on a normal portrait crop.
    const sw = img.naturalWidth * 0.34;
    const sh = img.naturalHeight * 0.20;
    const sx = (img.naturalWidth - sw) / 2;
    const sy = img.naturalHeight * 0.28;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 64, 64);
    const d = ctx.getImageData(0, 0, 64, 64).data;
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
    return [r / n, g / n, b / n];
  }

  async function passesQualityGate(inputSrc, outputSrc) {
    try {
      const [a, b] = await Promise.all([faceTone(inputSrc), faceTone(outputSrc)]);
      const delta = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
      return { pass: delta <= TONE_TOLERANCE, delta };
    } catch {
      return { pass: true, delta: null };   // never block on a measurement failure
    }
  }

  /* ---------- generation ---------- */

  /* opts: { endpoint, photo|modelId, wigImage, productId, productTitle, variantId, texture, capType } */
  async function generate(opts, onProgress) {
    const key = lookKey(opts);

    if (opts.photo) {
      const cached = store.getLook(key);
      if (cached) {
        track('wig_tryon_generated', {
          productId: opts.productId, productTitle: opts.productTitle,
          variantId: opts.variantId, source: 'own_photo', cache: 'session', ms: 0
        });
        return { image: cached.image, cached: true };
      }
    }

    const started = Date.now();
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt === 1 && onProgress) onProgress('One more try…');

      let res;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        res = await fetch(opts.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: ctrl.signal,
          body: JSON.stringify({
            mode: opts.photo ? 'upload' : 'preset',
            photo: opts.photo || undefined,
            modelId: opts.modelId || undefined,
            modelImage: opts.modelImage || undefined,
            wigImage: opts.wigImage,
            productId: opts.productId,
            productTitle: opts.productTitle,
            texture: opts.texture,
            capType: opts.capType,
            attempt
          })
        });
      } catch (e) {
        clearTimeout(timer);
        throw new Error(e.name === 'AbortError'
          ? 'That took too long. Try again in a moment.'
          : 'Could not reach the try-on service.');
      }
      clearTimeout(timer);

      if (res.status === 429) {
        track('wig_tryon_failed', { productId: opts.productId, reason: 'rate_limited' });
        throw new Error('Too many try-ons right now. Give it a minute.');
      }
      if (res.status === 503) {
        // Daily budget spent. Presets still work; new generations are paused.
        track('wig_tryon_failed', { productId: opts.productId, reason: 'budget_exhausted' });
        const err = new Error('Try-on is busy today. Have a look on one of our models instead.');
        err.degraded = true;
        throw err;
      }
      if (!res.ok) { lastError = new Error('The try-on service is unavailable right now.'); continue; }

      const data = await res.json();
      if (!data.image) { lastError = new Error('No preview came back. Try a clearer, front-facing photo.'); continue; }

      // Presets are QA'd once at pre-generation time, not on every serve.
      if (!opts.photo) {
        track('wig_tryon_generated', {
          productId: opts.productId, productTitle: opts.productTitle,
          variantId: opts.variantId, source: 'model', modelId: opts.modelId,
          cache: data.cached ? 'r2' : 'fresh', ms: Date.now() - started
        });
        return { image: data.image, cached: !!data.cached };
      }

      /* Tone check is advisory only. It samples a fixed centre band, so framing
         differences between input and output move it more than real skin drift
         does. Retrying on it doubled every cost and rarely changed the result;
         we measure it, surface it, and track it instead. */
      const gate = await passesQualityGate(opts.photo, data.image);
      {
        const record = {
          image: data.image,
          title: opts.productTitle,
          variantId: opts.variantId,
          url: opts.productUrl,
          at: Date.now(),
          flagged: !gate.pass
        };
        store.setLook(key, record);
        track('wig_tryon_generated', {
          productId: opts.productId, productTitle: opts.productTitle,
          variantId: opts.variantId, source: 'own_photo', cache: 'fresh',
          ms: Date.now() - started, retried: attempt > 0,
          toneDelta: gate.delta == null ? null : Math.round(gate.delta),
          flagged: !gate.pass
        });
        return { image: data.image, flagged: !gate.pass };
      }
    }
    track('wig_tryon_failed', {
      productId: opts.productId, productTitle: opts.productTitle,
      reason: lastError ? lastError.message : 'unknown'
    });
    throw lastError || new Error('Try-on failed.');
  }

  function lookKey(opts) {
    return [opts.productId, opts.variantId, opts.modelId || 'me'].join(':');
  }

  /* ---------- output ---------- */

  async function watermark(src, title) {
    const img = await load(src);
    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const pad = Math.round(c.width * 0.035);
    const size = Math.max(14, Math.round(c.width * 0.032));
    const barH = size * 2.9;

    const grad = ctx.createLinearGradient(0, c.height - barH * 1.6, 0, c.height);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.62)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, c.height - barH * 1.6, c.width, barH * 1.6);

    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'alphabetic';
    ctx.font = `600 ${size}px -apple-system, Segoe UI, Roboto, sans-serif`;
    ctx.fillText(truncate(title, 34), pad, c.height - pad - size * 1.1);
    ctx.font = `400 ${Math.round(size * 0.72)}px -apple-system, Segoe UI, Roboto, sans-serif`;
    ctx.globalAlpha = 0.85;
    ctx.fillText('privatelabelextensions.com', pad, c.height - pad);

    return c.toDataURL('image/jpeg', 0.92);
  }

  async function download(src, title) {
    track('wig_tryon_downloaded', { productTitle: title });
    const a = document.createElement('a');
    a.href = await watermark(src, title);
    a.download = slug(title) + '-try-on.jpg';
    a.click();
  }

  async function share(src, title) {
    const marked = await watermark(src, title);
    const blob = await (await fetch(marked)).blob();
    const file = new File([blob], slug(title) + '-try-on.jpg', { type: 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: `${title} — tried on at privatelabelextensions.com` });
      track('wig_tryon_shared', { productTitle: title });
      return true;
    }
    return false;
  }

  async function saveLook({ endpoint, email, productId, productTitle, variantId, url }) {
    const res = await fetch(endpoint.replace(/\/generate$/, '/save-look'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, productId, productTitle, variantId, url })
    });
    if (!res.ok) throw new Error('Could not save that. Check the address and try again.');
    track('wig_tryon_emailed', { productId, productTitle, variantId });
    return true;
  }

  /* ---------- utils ---------- */

  function load(src) {
    return new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = src;
    });
  }

  const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const truncate = (s, n) => (String(s).length > n ? String(s).slice(0, n - 1) + '…' : String(s));

  return { store, downscale, checkFace, generate, download, share, saveLook, watermark, lookKey, track, bucket, TRAY_MAX };
})();
